import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, ShieldCheck } from 'lucide-react';
import { loadRoles, saveRole, deleteRole } from './data.js';
import { friendlyError } from './lib/format.js';

// ── Menu items yang bisa di-toggle per role ──────────────────────────────────
const MENU_GROUPS = [
  {
    group: 'Operasional Klinik',
    items: [
      { key: 'dashboard',    label: 'Dashboard' },
      { key: 'followup',     label: 'Follow-Up Pasien' },
      { key: 'registration', label: 'Pendaftaran' },
      { key: 'nakesboard',   label: 'Pemeriksaan (Nakes Board)' },
      { key: 'payments',     label: 'Pembayaran / Kasir' },
    ],
  },
  {
    group: 'Layanan & Data Medis',
    items: [
      { key: 'packages',      label: 'Paket Layanan' },
      { key: 'medicalRecords', label: 'Rekam Medis' },
      { key: 'clients',       label: 'Data Pasien' },
      { key: 'memberMgm',     label: 'Membership (MGM, Survey, Voucher)' },
    ],
  },
  {
    group: 'SDM & Operasional',
    items: [
      { key: 'bhp',        label: 'Inventory / BHP' },
      { key: 'farmasi',    label: 'Farmasi / Apotik' },
      { key: 'attendance', label: 'Kehadiran' },
      { key: 'reports',    label: 'Laporan' },
      { key: 'payroll',    label: 'Payroll' },
    ],
  },
  {
    group: 'Sistem',
    items: [
      { key: 'settings', label: 'Pengaturan Klinik' },
    ],
  },
];

// Role bawaan yang tidak boleh dihapus
const SYSTEM_ROLES = new Set(['Admin', 'Nakes', 'Nakes Plus', 'Kasir', 'Frontliner']);

const emptyRole = {
  id:                    '',
  role_name:             '',
  menu_access:           [],
  can_see_revenue_today: false,
  can_see_revenue_month: false,
};

export function RolesPage() {
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null); // role yang sedang diedit
  const [form, setForm]           = useState(emptyRole);
  const [isNew, setIsNew]         = useState(false);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [error, setError]         = useState('');
  const [status, setStatus]       = useState(''); // 'ok' | 'err'

  async function fetch() {
    setLoading(true);
    setError('');
    try {
      const data = await loadRoles();
      setRoles(data);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetch(); }, []);

  function selectRole(role) {
    setSelected(role.id);
    setForm({
      id:                    role.id,
      role_name:             role.role_name,
      menu_access:           Array.isArray(role.menu_access) ? role.menu_access : [],
      can_see_revenue_today: role.can_see_revenue_today  || false,
      can_see_revenue_month: role.can_see_revenue_month  || false,
    });
    setIsNew(false);
    setStatus('');
    setError('');
  }

  function startNew() {
    setSelected('__new__');
    setForm({ ...emptyRole });
    setIsNew(true);
    setStatus('');
    setError('');
  }

  function toggleMenu(key) {
    setForm((prev) => {
      const cur = prev.menu_access || [];
      return {
        ...prev,
        menu_access: cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key],
      };
    });
    setStatus('');
  }

  function toggleAll(keys, checked) {
    setForm((prev) => {
      const cur = new Set(prev.menu_access || []);
      keys.forEach((k) => checked ? cur.add(k) : cur.delete(k));
      return { ...prev, menu_access: [...cur] };
    });
    setStatus('');
  }

  async function handleSave() {
    if (!form.role_name.trim()) { setError('Nama role wajib diisi.'); return; }
    if (isNew && roles.find((r) => r.id === form.role_name.trim())) {
      setError('Nama role sudah ada.'); return;
    }
    setSaving(true); setError(''); setStatus('');
    try {
      const payload = {
        ...form,
        id:        isNew ? form.role_name.trim() : form.id,
        role_name: form.role_name.trim(),
      };
      await saveRole(payload);
      await fetch();
      setSelected(payload.id);
      setIsNew(false);
      setStatus('ok');
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!selected || SYSTEM_ROLES.has(selected)) return;
    if (!window.confirm(`Hapus role "${form.role_name}"? Pengguna dengan role ini tidak akan bisa login.`)) return;
    setDeleting(true); setError('');
    try {
      await deleteRole(selected);
      setSelected(null);
      setForm(emptyRole);
      setIsNew(false);
      await fetch();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setDeleting(false);
    }
  }

  const isSystemRole = selected && SYSTEM_ROLES.has(selected) && !isNew;

  return (
    <div className="roles-layout">

      {/* ── Sidebar: daftar role ── */}
      <div className="roles-sidebar">
        <div className="roles-sidebar-head">
          <span className="eyebrow">Daftar Role</span>
          <button className="button button-primary" style={{ padding: '.45rem .8rem', fontSize: 12 }} onClick={startNew}>
            <Plus size={13} /> Tambah
          </button>
        </div>

        {loading ? (
          <div style={{ padding: '1rem', color: '#9ca3af', fontSize: 13 }}>Memuat…</div>
        ) : (
          <div className="roles-list">
            {roles.map((role) => (
              <button
                key={role.id}
                type="button"
                className={`roles-list-item ${selected === role.id ? 'roles-list-item-active' : ''}`}
                onClick={() => selectRole(role)}
              >
                <div className="roles-list-name">{role.role_name}</div>
                <div className="roles-list-count">
                  {(Array.isArray(role.menu_access) ? role.menu_access : []).length} akses
                </div>
                {SYSTEM_ROLES.has(role.id) && (
                  <span className="roles-system-badge">Sistem</span>
                )}
              </button>
            ))}
            {!roles.length && (
              <div style={{ padding: '1rem', color: '#9ca3af', fontSize: 13 }}>Belum ada role.</div>
            )}
          </div>
        )}
      </div>

      {/* ── Editor ── */}
      <div className="roles-editor">
        {!selected ? (
          <div className="roles-empty-state">
            <ShieldCheck size={40} style={{ opacity: .25 }} />
            <p>Pilih role dari daftar atau tambah role baru.</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="roles-editor-head">
              <div>
                <span className="eyebrow">{isNew ? 'Role Baru' : 'Edit Role'}</span>
                <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{isNew ? 'Tambah Role' : form.role_name}</h3>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {!isNew && !isSystemRole && (
                  <button className="button" style={{ background: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}
                    onClick={handleDelete} disabled={deleting || saving}>
                    <Trash2 size={14} /> {deleting ? 'Menghapus…' : 'Hapus'}
                  </button>
                )}
                <button className="button button-primary" onClick={handleSave} disabled={saving || deleting}>
                  <Save size={14} /> {saving ? 'Menyimpan…' : 'Simpan Role'}
                </button>
              </div>
            </div>

            {error && <div className="alert alert-danger" style={{ margin: '0 0 1rem' }}>{error}</div>}
            {status === 'ok' && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '.6rem 1rem', fontSize: 13, color: '#166534', marginBottom: '1rem' }}>
                ✓ Role berhasil disimpan.
              </div>
            )}

            {/* Nama role */}
            <div className="form-field" style={{ marginBottom: '1.25rem' }}>
              <label>Nama Role</label>
              {isSystemRole ? (
                <input value={form.role_name} readOnly style={{ background: '#f8fafc', color: '#6b7280' }} />
              ) : (
                <input
                  value={form.role_name}
                  onChange={(e) => { setForm((p) => ({ ...p, role_name: e.target.value })); setStatus(''); }}
                  placeholder="Contoh: Dokter Estetik, Staf Gudang…"
                />
              )}
              {isSystemRole && (
                <small style={{ color: '#9ca3af' }}>Role sistem tidak dapat diganti namanya.</small>
              )}
            </div>

            {/* Akses Menu */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '.75rem' }}>
                Akses Menu
              </div>
              {MENU_GROUPS.map((group) => {
                const groupKeys = group.items.map((i) => i.key);
                const allChecked = groupKeys.every((k) => (form.menu_access || []).includes(k));
                return (
                  <div key={group.group} className="roles-perm-group">
                    <div className="roles-perm-group-head">
                      <span>{group.group}</span>
                      <label className="roles-perm-toggle-all">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={(e) => toggleAll(groupKeys, e.target.checked)}
                          disabled={isSystemRole}
                        />
                        Semua
                      </label>
                    </div>
                    <div className="roles-perm-grid">
                      {group.items.map((item) => {
                        const checked = (form.menu_access || []).includes(item.key);
                        return (
                          <label key={item.key} className={`roles-perm-item ${checked ? 'roles-perm-item-on' : ''}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleMenu(item.key)}
                              disabled={isSystemRole}
                            />
                            {item.label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Izin Khusus */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '.75rem' }}>
                Izin Khusus
              </div>
              <div className="roles-perm-group">
                <div className="roles-perm-grid">
                  <label className={`roles-perm-item ${form.can_see_revenue_today ? 'roles-perm-item-on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={form.can_see_revenue_today || false}
                      onChange={(e) => { setForm((p) => ({ ...p, can_see_revenue_today: e.target.checked })); setStatus(''); }}
                      disabled={isSystemRole}
                    />
                    Lihat Omzet Hari Ini
                  </label>
                  <label className={`roles-perm-item ${form.can_see_revenue_month ? 'roles-perm-item-on' : ''}`}>
                    <input
                      type="checkbox"
                      checked={form.can_see_revenue_month || false}
                      onChange={(e) => { setForm((p) => ({ ...p, can_see_revenue_month: e.target.checked })); setStatus(''); }}
                      disabled={isSystemRole}
                    />
                    Lihat Omzet Bulan Ini
                  </label>
                </div>
              </div>
            </div>

            {isSystemRole && (
              <div style={{ marginTop: '1rem', padding: '.75rem 1rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10, fontSize: 13, color: '#854d0e' }}>
                ⚠ Role sistem tidak dapat diedit. Untuk menyesuaikan akses, buat role baru berdasarkan role ini.
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .roles-layout { display: flex; gap: 0; min-height: 400px; background: #f8fafc; border-radius: 14px; border: 1px solid #e5e7eb; overflow: hidden; }
        .roles-sidebar { width: 220px; flex-shrink: 0; border-right: 1px solid #e5e7eb; background: #fff; display: flex; flex-direction: column; }
        .roles-sidebar-head { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1rem .75rem; border-bottom: 1px solid #f3f4f6; }
        .roles-list { flex: 1; overflow-y: auto; padding: .5rem; }
        .roles-list-item { width: 100%; text-align: left; background: none; border: none; border-radius: 8px; padding: .6rem .75rem; cursor: pointer; transition: background .1s; position: relative; margin-bottom: 2px; }
        .roles-list-item:hover { background: #f9fafb; }
        .roles-list-item-active { background: #f0fdf4 !important; }
        .roles-list-name { font-size: 13px; font-weight: 600; color: #111827; }
        .roles-list-item-active .roles-list-name { color: #166534; }
        .roles-list-count { font-size: 11px; color: #9ca3af; margin-top: 1px; }
        .roles-system-badge { position: absolute; top: 6px; right: 8px; font-size: 9px; font-weight: 700; background: #e5e7eb; color: #6b7280; padding: 2px 6px; border-radius: 999px; text-transform: uppercase; }
        .roles-editor { flex: 1; padding: 1.5rem; overflow-y: auto; }
        .roles-empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 300px; gap: .75rem; color: #9ca3af; text-align: center; }
        .roles-editor-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; margin-bottom: 1.25rem; }
        .roles-perm-group { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; margin-bottom: .75rem; overflow: hidden; }
        .roles-perm-group-head { display: flex; align-items: center; justify-content: space-between; padding: .6rem 1rem; background: #f9fafb; border-bottom: 1px solid #e5e7eb; font-size: 12px; font-weight: 700; color: #374151; }
        .roles-perm-toggle-all { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; color: #6b7280; cursor: pointer; }
        .roles-perm-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0; }
        .roles-perm-item { display: flex; align-items: center; gap: 8px; padding: .6rem 1rem; font-size: 13px; color: #374151; cursor: pointer; border-bottom: 1px solid #f3f4f6; transition: background .1s; }
        .roles-perm-item:last-child { border-bottom: none; }
        .roles-perm-item:hover { background: #f9fafb; }
        .roles-perm-item-on { background: #f0fdf4; color: #166534; }
        .roles-perm-item input[type="checkbox"] { accent-color: #16a34a; width: 15px; height: 15px; flex-shrink: 0; }
      `}</style>
    </div>
  );
}
